import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

/**
 * First-run / no-results empty-state panel with an optional call to
 * action. Replaces the disabled-primary-button pattern that was
 * dead-ending bootstrap flows ("why is my New-organization button
 * grey?").
 *
 * Usage:
 *   <EmptyState title="No organizations yet"
 *               description="Organizations group locations…"
 *               icon={Store}
 *               cta={{ href: "/partners", label: "Go to Partners" }} />
 *
 *   <EmptyState title="No audit entries"
 *               description="Nothing has been audited in this range." />
 */
export interface EmptyStateProps {
  title: string;
  description?: string;
  /** Optional lucide icon rendered at the top. */
  icon?: LucideIcon;
  /**
   * Primary CTA. The type requires at least one of `href` or `onClick`
   * - a CTA with neither would render a button with an undefined
   * click handler (silent no-op). `href` wins when both are provided.
   */
  cta?: CtaConfig;
  /** Secondary CTA rendered next to the primary. Same shape. */
  secondaryCta?: CtaConfig;
  className?: string;
}

/** Discriminated CTA shape - at least one of href / onClick required. */
type CtaConfig =
  | { label: string; href: string; onClick?: () => void }
  | { label: string; href?: undefined; onClick: () => void };

export function EmptyState(props: EmptyStateProps): JSX.Element {
  const Icon = props.icon;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center gap-3 rounded-lg border border-dashed bg-muted/30 px-4 py-8 text-center sm:py-10",
        props.className,
      )}
    >
      {Icon && (
        <div className="rounded-full bg-muted p-3 text-muted-foreground">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
      )}
      <div className="max-w-prose space-y-1">
        <h3 className="text-sm font-semibold">{props.title}</h3>
        {props.description && <p className="text-sm text-muted-foreground">{props.description}</p>}
      </div>
      {(props.cta || props.secondaryCta) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {props.cta && <Cta {...props.cta} variant="primary" />}
          {props.secondaryCta && <Cta {...props.secondaryCta} variant="secondary" />}
        </div>
      )}
    </div>
  );
}

function Cta(props: CtaConfig & { variant: "primary" | "secondary" }): JSX.Element {
  const cls = cn(
    "rounded-md px-3 py-1.5 text-sm font-medium",
    props.variant === "primary"
      ? "bg-primary text-primary-foreground hover:opacity-90"
      : "border bg-background text-foreground hover:bg-muted",
  );
  if (props.href !== undefined) {
    return (
      <Link to={props.href} className={cls}>
        {props.label}
      </Link>
    );
  }
  return (
    <button type="button" onClick={props.onClick} className={cls}>
      {props.label}
    </button>
  );
}
